import type { ReactNode } from "react";
import {
  SITE_URL,
  baseCrumbs,
  sharedRelated,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
} from "./learnShared";
import type { LearnPageData } from "./learnShared";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

/* ── Page 3 ── */

const p3Faqs: LearnFAQ[] = [
  {
    question: "What is the difference between accelerated TMS and regular TMS?",
    answer:
      "Regular TMS delivers one session a day, five days a week, for about four to six weeks. Accelerated TMS delivers two or more sessions a day, compressing the course into one or two weeks. The sessions themselves are usually short theta burst sessions of about 3 to 10 minutes with rest breaks between them. The protocols vary widely between clinics, and only the SAINT® protocol has its own FDA clearance; most other accelerated schedules are off-label uses of FDA-cleared devices.",
  },
  {
    question: "Is accelerated TMS FDA-approved?",
    answer:
      "TMS devices are FDA-cleared, not approved, and clearance is specific to a protocol and indication. The only accelerated protocol cleared as such is the Magnus Neuromodulation System with SAINT® Technology (510(k) K220177, September 2022): ten iTBS sessions a day for five days with MRI-based targeting, for adults with major depressive disorder who did not improve enough on medication. Other accelerated schedules use FDA-cleared devices off-label, which is legal but means the FDA has not reviewed that schedule and insurers often do not cover it.",
  },
  {
    question: "Is there a one-day TMS treatment?",
    answer:
      "Not one that is FDA-cleared. No single-day TMS protocol for depression has been cleared by the FDA. Some research groups have studied delivering many sessions within one day, but these remain experimental. The shortest FDA-cleared course is SAINT®, which takes five consecutive days. If a clinic offers a one-day course, ask what evidence supports it and understand that it is an experimental, off-label schedule.",
  },
  {
    question: "How long is a theta burst TMS session?",
    answer:
      "A standard intermittent theta burst (iTBS) session delivers about 600 pulses in roughly 3 minutes, compared with 37.5 minutes for the conventional 10 Hz protocol used in the THREE-D trial. Including setup and positioning, a once-daily iTBS visit usually takes about 15 to 20 minutes. In the SAINT® protocol, each session is longer, about 10 minutes, because it delivers 1,800 pulses.",
  },
  {
    question: "Does theta burst TMS work as well as regular TMS?",
    answer:
      "The strongest evidence says yes for the once-daily schedule. The THREE-D trial randomized 414 adults with treatment-resistant depression to iTBS or 10 Hz TMS and found iTBS non-inferior, with depression scores falling by about the same amount in both groups and similar side effects. The trial compared two active treatments without a sham arm, so it shows equivalence rather than efficacy against placebo, and its results apply to once-daily treatment rather than accelerated schedules.",
  },
  {
    question: "What are the side effects of accelerated TMS?",
    answer:
      "The side effects are the same as for standard TMS: scalp discomfort, headache, and facial twitching, which usually fade within the first days. Headache was reported by about two-thirds of participants in the THREE-D trial, and iTBS was rated slightly more painful than 10 Hz. Seizure is the most serious risk and is rare with standard parameters. Because accelerated protocols deliver many more pulses per day and have been studied in far fewer people, rare risks are less well characterized than for conventional TMS.",
  },
  {
    question: "How much does accelerated TMS cost?",
    answer:
      "Cost depends on the protocol and on whether your insurance covers it. A standard once-daily course is more often covered when plan criteria are met. SAINT® uses its own Category III CPT codes, and coverage varies by plan; many patients currently self-pay. Off-label accelerated schedules are frequently not reimbursed. Neuro Wellness TMS Centers of America verifies your specific plan before treatment; see our cost and insurance page for details on codes, criteria, and what to ask.",
  },
  {
    question: "Can I travel to Coral Springs for accelerated TMS?",
    answer:
      "Yes. Because the SAINT® course takes five consecutive days, patients from elsewhere in Florida and out of state do travel to Neuro Wellness TMS Centers of America in Coral Springs for treatment. Plan to arrive the day before treatment starts, stay locally for the week, and expect full days at the clinic with roughly 50-minute breaks between sessions. A pre-treatment evaluation and MRI must be completed beforehand, and a follow-up plan with your home providers should be arranged before you leave.",
  },
];

export const page3: LearnPageData = {
  slug: "accelerated-tms-theta-burst-stimulation",
  title: "Accelerated TMS & Theta Burst (iTBS): What to Know",
  description:
    "Accelerated TMS and theta burst (iTBS) explained: 3-minute sessions, multi-session days, the THREE-D trial, FDA-cleared vs off-label protocols, and cost.",
  canonical: "/learn/accelerated-tms-theta-burst-stimulation",
  h1: "What Is Accelerated TMS and Theta Burst Stimulation (iTBS)?",
  breadcrumbLabel: "Accelerated TMS & Theta Burst",
  crumbs: baseCrumbs("Accelerated TMS & Theta Burst"),
  shortAnswer:
    "Accelerated TMS delivers multiple sessions per day to compress treatment into 1–2 weeks. Theta burst (iTBS) is a 3-minute pulse pattern shown equivalent to standard 19-minute TMS. Only SAINT® has its own FDA clearance as an accelerated protocol. Call Neuro Wellness TMS Centers of America at 954-827-9793.",
  keyTakeaways: [
    "Accelerated TMS = more than one session per day, compressing the course to 1–2 weeks.",
    "iTBS delivers ~600 pulses in ~3 minutes; the THREE-D trial (n=414) found it non-inferior to 10 Hz TMS.",
    "Only SAINT® (Magnus, K220177) is FDA-cleared as an accelerated protocol; other accelerated schedules are off-label.",
    "No one-day TMS protocol is FDA-cleared; the shortest cleared course is SAINT® at 5 days.",
    "Side effects are the same as standard TMS; rare risks are less characterized for accelerated schedules.",
  ],
  sections: [
    {
      heading: "What theta burst stimulation (iTBS) is",
      body: (
        <>
          {P(
            "Intermittent theta burst stimulation (iTBS) is a TMS pulse pattern that delivers bursts of 3 pulses at 50 Hz, repeated at 5 Hz — 2 seconds on, 8 seconds off — for about 600 pulses in roughly 3 minutes. This is much shorter than the conventional 10 Hz protocol, which runs about 19 to 37 minutes.",
          )}
          {P(
            "The THREE-D trial randomized 414 adults with treatment-resistant depression to iTBS or 10 Hz TMS and found iTBS non-inferior, with depression scores falling by about the same amount in both groups. iTBS was FDA-cleared as a once-daily protocol for major depressive disorder in 2018.",
          )}
        </>
      ),
    },
    {
      heading: "What accelerated TMS means",
      body: (
        <>
          {P(
            "Accelerated TMS is any schedule that delivers more than one session per day, compressing the course from 4–6 weeks into 1–2 weeks. Protocols vary widely — from 2 to 10 sessions per day — and the sessions are usually short iTBS sessions of 3 to 10 minutes with rest breaks between them.",
          )}
          {P(
            "Only the SAINT® protocol (Magnus Neuromodulation System, 510(k) K220177) has its own FDA clearance as an accelerated protocol: ten iTBS sessions per day for five days with fMRI targeting. Other accelerated schedules are off-label uses of FDA-cleared devices — legal, but the FDA has not reviewed that schedule and insurers often do not cover it.",
          )}
        </>
      ),
    },
    {
      heading: "Regulatory status: cleared vs off-label",
      body: (
        <div className="my-6 rounded-xl border border-medium-blue/30 bg-medium-blue/5 p-5">
          <p>
            <strong>Regulatory status.</strong> TMS devices are FDA-cleared, not
            approved. Clearance is specific to a protocol and indication. The
            only accelerated protocol cleared as such is SAINT® (K220177,
            September 2022). Other accelerated schedules use FDA-cleared devices
            off-label, which is legal but means the FDA has not reviewed that
            schedule and insurers often do not cover it. No single-day TMS
            protocol for depression has been cleared by the FDA.
          </p>
        </div>
      ),
    },
    {
      heading: "The THREE-D trial evidence",
      body: (
        <>
          {P(
            "The THREE-D trial is the key study behind iTBS. It randomized 414 adults with treatment-resistant depression to iTBS or 10 Hz rTMS and found iTBS non-inferior, with similar depression score reductions and similar side effects. The trial compared two active treatments without a sham arm, so it shows equivalence rather than efficacy against placebo, and its results apply to once-daily treatment rather than accelerated schedules.",
          )}
        </>
      ),
    },
    {
      heading: "Cost and insurance",
      body: (
        <>
          {P(
            "A standard once-daily course is more often covered when plan criteria are met. SAINT® uses its own Category III CPT codes (0889T–0892T), and coverage varies by plan; many patients currently self-pay. Off-label accelerated schedules are frequently not reimbursed. Neuro Wellness TMS Centers of America verifies your specific plan before treatment.",
          )}
        </>
      ),
    },
  ],
  faqs: p3Faqs,
  sources: [
    {
      citation:
        "Blumberger DM, et al. Effectiveness of theta burst versus high-frequency repetitive transcranial magnetic stimulation in patients with depression (THREE-D): a randomised non-inferiority trial. Lancet. 2018;391(10131):1683-1692.",
      url: "https://doi.org/10.1016/S0140-6736(18)30295-2",
    },
    {
      citation:
        "Cole EJ, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for Treatment-Resistant Depression. Am J Psychiatry. 2020;177(8):716-726.",
      url: "https://doi.org/10.1176/appi.ajp.2019.19070720",
    },
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://doi.org/10.1176/appi.ajp.2021.20101429",
    },
  ],
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/learn/accelerated-tms-theta-burst-stimulation#webpage`,
        name: "What Is Accelerated TMS and Theta Burst Stimulation (iTBS)?",
        url: `${SITE_URL}/learn/accelerated-tms-theta-burst-stimulation`,
        description:
          "Accelerated TMS and theta burst (iTBS) explained: 3-minute sessions, multi-session days, the THREE-D trial, FDA-cleared vs off-label protocols, and cost.",
        about: [
          {
            "@id": `${SITE_URL}/learn/accelerated-tms-theta-burst-stimulation#itbs`,
          },
          {
            "@id": `${SITE_URL}/learn/accelerated-tms-theta-burst-stimulation#accelerated`,
          },
          { "@id": `${SITE_URL}/#condition-mdd` },
          { "@id": `${SITE_URL}/#condition-trd` },
        ],
      }),
      orgNode,
      {
        "@type": "MedicalTherapy",
        "@id": `${SITE_URL}/learn/accelerated-tms-theta-burst-stimulation#itbs`,
        name: "Intermittent theta burst stimulation (iTBS)",
        alternateName: ["Theta burst TMS", "Theta burst stimulation", "TBS"],
        description:
          "A transcranial magnetic stimulation pulse pattern delivering bursts of 3 pulses at 50 Hz repeated at 5 Hz, 2 seconds on and 8 seconds off, about 600 pulses in roughly 3 minutes per session. Shown non-inferior to 10 Hz rTMS in the THREE-D trial (n=414) and FDA-cleared as a once-daily protocol for major depressive disorder in 2018.",
        relevantSpecialty: "Psychiatry",
        indication: {
          "@type": "MedicalIndication",
          name: "Major depressive disorder in adults who have not achieved satisfactory improvement from antidepressant medication",
        },
        contraindication: [
          {
            "@type": "MedicalContraindication",
            name: "Ferromagnetic metal in or near the head",
          },
          {
            "@type": "MedicalContraindication",
            name: "Implanted electronic stimulators",
          },
          {
            "@type": "MedicalContraindication",
            name: "Unevaluated seizure history",
          },
        ],
        seriousAdverseOutcome: {
          "@type": "MedicalEntity",
          name: "Seizure (rare)",
        },
        adverseOutcome: [
          { "@type": "MedicalEntity", name: "Headache" },
          { "@type": "MedicalEntity", name: "Scalp discomfort" },
          { "@type": "MedicalEntity", name: "Facial muscle twitching" },
        ],
        study: [
          {
            "@type": "MedicalStudy",
            name: "Blumberger DM, et al. Effectiveness of theta burst versus high-frequency repetitive transcranial magnetic stimulation in patients with depression (THREE-D): a randomised non-inferiority trial. Lancet. 2018;391(10131):1683-1692.",
            url: "https://doi.org/10.1016/S0140-6736(18)30295-2",
          },
        ],
      },
      {
        "@type": "MedicalTherapy",
        "@id": `${SITE_URL}/learn/accelerated-tms-theta-burst-stimulation#accelerated`,
        name: "Accelerated transcranial magnetic stimulation",
        alternateName: [
          "Accelerated TMS",
          "Rapid TMS",
          "Stanford Accelerated Intelligent Neuromodulation Therapy (SAINT®)",
        ],
        description:
          "TMS delivered as more than one session per day over one to two weeks. Protocols vary from 2 to 10 sessions per day. The Magnus Neuromodulation System with SAINT Technology (10 iTBS sessions per day for 5 days with fMRI targeting) is FDA-cleared (510(k) K220177); other accelerated schedules are off-label uses of FDA-cleared devices.",
        relevantSpecialty: "Psychiatry",
        study: [
          {
            "@type": "MedicalStudy",
            name: "Cole EJ, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for Treatment-Resistant Depression. Am J Psychiatry. 2020;177(8):716-726.",
            url: "https://doi.org/10.1176/appi.ajp.2019.19070720",
          },
          {
            "@type": "MedicalStudy",
            name: "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
            url: "https://doi.org/10.1176/appi.ajp.2021.20101429",
          },
        ],
      },
      {
        "@type": "MedicalCondition",
        "@id": `${SITE_URL}/#condition-mdd`,
        name: "Major depressive disorder",
        code: { "@type": "MedicalCode", code: "D003865", codingSystem: "MeSH" },
      },
      {
        "@type": "MedicalCondition",
        "@id": `${SITE_URL}/#condition-trd`,
        name: "Treatment-resistant depression",
      },
      faqJsonld(
        p3Faqs,
        `${SITE_URL}/learn/accelerated-tms-theta-burst-stimulation#faq`,
      ),
      breadcrumbJsonld(baseCrumbs("Accelerated TMS & Theta Burst")),
    ],
  },
  relatedLinks: sharedRelated,
};
